import { Suspense, useEffect, useMemo } from "react";
import {
  Outlet,
  isRouteErrorResponse,
  useLocation,
  useParams,
  useRouteError,
} from "react-router-dom";
import { client } from "../../../client";
import { FadeInUpContainer, NavigationBar } from "../../../components";
import { useGetOrganization } from "../../../services";
import { addClientTab, addMemberTab } from "../../../state/tabs";
import { getImageUrl } from "../../../utils/image";
import { isAxiosError } from "../../../utils/query-client";
import { useTheme } from "../../../contexts";
import { Loading } from "./components/Loading";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: We scroll to top when the pathname changes
  useEffect(() => {
    const timeoutId = setTimeout(() => window.scrollTo(0, 0), 10);
    return () => clearTimeout(timeoutId);
  }, [pathname]);

  return null;
};

const SuspenseTiming = () => {
  useEffect(() => {
    const navStart = Number(sessionStorage.getItem("dashboard-nav-start") ?? 0);
    const t = () =>
      navStart ? (performance.now() - navStart).toFixed(0) : "?";
    console.log(`[LAYOUT] content committed (useEffect) at ${t()}ms`);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        console.log(`[LAYOUT] browser painted new frame at ${t()}ms`);
      });
    });
  }, []);
  return null;
};

const AddToState = () => {
  const { memberId, clientId } = useParams();

  useEffect(() => {
    if (!memberId) return;
    // Add the member tab
    addMemberTab(memberId);
  }, [memberId]);

  useEffect(() => {
    if (!clientId) return;
    // Add the client tab
    addClientTab(clientId);
  }, [clientId]);

  return null;
};

// TODO: Move this to App.tsx ?
const apiBaseUrl = process.env.REACT_APP_BASE_URL
  ? String(process.env.REACT_APP_BASE_URL)
  : "https://profiletool-api.onrender.com";

client.setConfig({
  baseUrl: apiBaseUrl,
});

client.interceptors.request.use((request, options) => {
  const token = localStorage.getItem("profiletool-auth-token");
  if (token) request.headers.set("Authorization", `Bearer ${token}`);
  return request;
});

client.interceptors.response.use((response) => {
  if (response.status === 401) {
    localStorage.removeItem("profiletool-auth-token");
    window.location.href = "/";
  }
  return response;
});

const DashboardLayout = () => {
  const { organizationId } = useParams();
  const { pathname } = useLocation();
  const { setOrganizationTheme } = useTheme();

  // TODO: Handle error better
  if (!organizationId) throw new Error("Organization ID is required");

  const { data } = useGetOrganization(organizationId);

  // Set organization theme when data loads (memoized to prevent infinite loops)
  const themeData = useMemo(() => {
    if (!data?.id) return null;
    return {
      organizationId: data.id,
      organizationName: data.name,
      colors: {
        // You can extract these from data or set defaults
        // For now using defaults, but you can modify based on your data structure
        primary: data.primaryColor || "000000", // You might have data.primaryColor
        secondary: data.secondaryColor || "#000000",
        accent: "#3b82f6",
      },
      logoId: data.logoId,
      iconLogoId: data.iconLogoId,
    };
  }, [
    data?.id,
    data?.name,
    data?.logoId,
    data?.iconLogoId,
    data?.primaryColor,
    data?.secondaryColor,
  ]);

  useEffect(() => {
    if (themeData) {
      setOrganizationTheme(themeData);
    }
  }, [themeData, setOrganizationTheme]);

  return (
    <div
      data-vaul-drawer-wrapper
      className="container mx-auto flex flex-col gap-4 md:gap-8 bg-gray-1"
    >
      {/* NavigationBar has its own boundary so a slow /v1/users doesn't block content */}
      <Suspense fallback={null}>
        <NavigationBar
          organizationLogo={
            data?.iconLogoId ? getImageUrl(data.iconLogoId) : undefined
          }
        />
      </Suspense>

      <Suspense fallback={<Loading />}>
        <FadeInUpContainer key={pathname}>
          <ScrollToTop />
          <AddToState />
          <SuspenseTiming />
          <Outlet />
        </FadeInUpContainer>
      </Suspense>
    </div>
  );
};

DashboardLayout.displayName = "DashboardLayout";

export const DashboardLayoutErrorBoundary = () => {
  const error = useRouteError();

  return isRouteErrorResponse(error) ? (
    <h1>
      {error.status} {error.statusText}
    </h1>
  ) : isAxiosError(error) ? (
    <h1>{error.code}</h1>
  ) : (
    <h1>Error</h1>
  );
};

export default DashboardLayout;
